import React from 'react';
import { Container } from './ui';
import Icon from './Icon';

const POS = [
  'left-1/2 top-0 -translate-x-1/2 -translate-y-1/2',
  'right-0 top-1/2 -translate-y-1/2 translate-x-1/4',
  'left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2',
  'left-0 top-1/2 -translate-y-1/2 -translate-x-1/4',
];
const DEFAULT_PERSONAS = ['Property Managers', 'Finance', 'Accounts Payable', 'Operations'];

// Sleek, graphical "Utility Bill Intelligence" hero: per-solution accent ring hub +
// circular pipeline nodes on a flowing line + outcome pill + chips.
export default function IntelligenceHero({ seg, actions }) {
  const intel = seg.intelligence;
  const personas = intel.personas || DEFAULT_PERSONAS;
  const accent = intel.accent || '#FF5757';
  const accent2 = intel.accent2 || '#ff8a5b';
  const ring = `conic-gradient(from 0deg, ${accent}, rgba(0,0,0,0) 25%, ${accent2} 55%, rgba(0,0,0,0) 80%, ${accent})`;
  const glow = `${accent}33`;
  const softBg = `${accent}26`;

  return (
    <section className="relative overflow-hidden border-b border-white/10 bg-brand-ink px-5 py-16 sm:px-8 sm:py-24">
      <div className="pointer-events-none absolute -left-40 -top-40 h-[30rem] w-[30rem] rounded-full blur-[130px]" style={{ background: glow }} />
      <div className="pointer-events-none absolute right-0 top-1/3 h-96 w-96 rounded-full blur-[130px]" style={{ background: `${accent}1a` }} />
      <div className="bp-grid-bg pointer-events-none absolute inset-0 opacity-20" />
      <Container className="relative">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-10">
          {/* text */}
          <div className="lg:col-span-7">
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: accent }}>
                <Icon name="Cpu" className="h-3.5 w-3.5" /> {intel.eyebrow}
              </span>
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{intel.audience}</span>
            </div>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.08] text-white sm:text-5xl">{seg.headline}</h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">{seg.intro}</p>
            {actions && <div className="mt-8 flex flex-col gap-3 sm:flex-row">{actions}</div>}
          </div>

          {/* animated accent ring hub */}
          <div className="lg:col-span-5">
            <div className="relative mx-auto aspect-square w-full max-w-[20rem]">
              <div className="bp-spin-slow absolute inset-0 rounded-full" style={{ background: ring }} />
              <div className="absolute inset-[7px] rounded-full border border-white/10 bg-brand-ink" />
              <div className="absolute inset-[7px] flex flex-col items-center justify-center rounded-full px-10 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ backgroundColor: softBg, color: accent }}><Icon name={intel.hubIcon || 'Users'} className="h-6 w-6" /></span>
                <div className="mt-3 font-display text-lg font-bold text-white">{intel.hubTitle || 'One workspace'}</div>
                <div className="mt-1.5 text-xs leading-relaxed text-slate-400">{intel.hubSub || 'for every multifamily team'}</div>
              </div>
              {personas.map((label, i) => (
                <span key={label} className={`absolute ${POS[i]} whitespace-nowrap rounded-full border border-white/15 bg-brand-ink px-3 py-1 text-xs font-semibold text-slate-200 shadow-lg`}>{label}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Pipeline — circular nodes on a flowing line */}
        <div className="relative mt-16 sm:mt-20">
          <div className="mb-8 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            <Icon name="Workflow" className="h-4 w-4" style={{ color: accent }} /> One bill · complete operational visibility
          </div>
          <div className="relative">
            <div className="absolute left-[10%] right-[10%] top-7 hidden h-0.5 overflow-hidden rounded-full bg-white/10 sm:block">
              <div className="bp-slide-x absolute top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full" style={{ backgroundColor: accent, boxShadow: `0 0 12px 2px ${accent}b3` }} />
            </div>
            <div className="relative grid grid-cols-2 gap-y-8 gap-x-6 sm:grid-cols-5">
              {intel.flow.map((f) => (
                <div key={f.label} className="flex flex-col items-center text-center" data-testid={`intel-flow-${f.label.toLowerCase()}`}>
                  <span className="flex h-14 w-14 items-center justify-center rounded-full border border-white/15 bg-brand-ink ring-4 ring-brand-ink" style={{ color: accent }}><Icon name={f.icon} className="h-5 w-5" /></span>
                  <span className="mt-3 text-sm font-bold text-white">{f.label}</span>
                  <span className="text-xs leading-tight text-slate-400">{f.sub}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-10 flex justify-center">
            <span className="inline-flex items-center gap-2.5 rounded-full px-6 py-3 font-display text-sm font-bold uppercase tracking-wide text-white shadow-lg sm:text-base" style={{ backgroundColor: accent, boxShadow: `0 10px 30px -8px ${accent}80` }}>
              <Icon name="TrendingUp" className="h-5 w-5" /> {intel.result}
            </span>
          </div>
        </div>

        {/* outcome chips */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-2.5">
          {intel.chips.map((c) => (
            <span key={c.label} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200">
              <Icon name={c.icon} className="h-4 w-4" style={{ color: accent }} /> {c.label}
            </span>
          ))}
        </div>
      </Container>
    </section>
  );
}
